import _ from 'underscore';
import { IReportMetadata } from '@app/shared/report/report-data.service';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { IObject } from '@type_definitions/datatypes';
import { IStoredReportDialogParamEvent, StoredReportDialogComponent } from './stored-report-dialog.component';
import { DialogSize, GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { util } from '@app/shared/util';
import { ReportGridService } from '@app/shared/report/report-grid.service';
import { IStoredReportBusy, STORED_REPORT_ADD_MODE, STORED_REPORT_EDIT_MODE } from '../report.component';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { IStoredReport } from '@app/shared/report/stored-report-data.service';

export interface IStoredReportDeleteEvent {
  reportId: number;
  storedReportId: number;
}

export interface IStoredReportChangeEvent {
  storedReport: IStoredReport;
  allowOverwriteByFilterStateParam?: boolean;
}

export interface IStoredReportSaveEvent extends IStoredReportDeleteEvent, IStoredReportChangeEvent { }

export interface IStoredReportDialogStateChangeEvent {
  isOpen: boolean;
  filters?: IObject;
}

@Component({
  selector: 'stored-report',
  template: `
    <div class="d-flex">
      <div *ngIf="isEditMode">
        <formly [model]="model" [fields]="fields" [form]="form" [inline]="true"></formly>
      </div>
      
      <div>
        <button class="btn btn-default align-top me-auto" (click)="editReport()" [disabled]="busy.loading || model?.storedReport?.editDisabled" [class.ms-3]="mode === '${STORED_REPORT_EDIT_MODE}'">
          {{buttonLabel || ('GA.BUTTONS.EDIT' | translate)}}
        </button>
      </div>
    </div>
  `
})
export class StoredReportComponent implements OnInit, OnChanges {
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model: { storedReport?:IStoredReport; };

  @Input() public metadata:IReportMetadata;
  @Input() public storedReports: IStoredReport[];
  @Input() public storedReportId: string | number;
  @Input() public busy: IStoredReportBusy = {};
  @Input() public reportId: number;
  @Input() public currentFilters: IObject = {};
  @Input() public mode: string;
  // if true then also render formly stored report selection fields
  @Input() public buttonLabel: string;

  @Output() public onChange: EventEmitter<IStoredReportChangeEvent> = new EventEmitter<IStoredReportChangeEvent>();
  @Output() public onDelete: EventEmitter<IStoredReportDeleteEvent> = new EventEmitter<IStoredReportDeleteEvent>();
  @Output() public onSave: EventEmitter<IStoredReportSaveEvent> = new EventEmitter<IStoredReportSaveEvent>();
  @Output() public onDialogStateChange: EventEmitter<IStoredReportDialogStateChangeEvent> = new EventEmitter<IStoredReportDialogStateChangeEvent>(true);

  private dialog: IDialog;
  private storedReportsSelect: IExtraFieldConfigurationObject;
  // This will be used when scenario of 'storedReport' and 'filters' param is in state params
  // On first change filters param will overwrite specified stored report filters
  // Select another stored report this will ignore 'filters' state params
  private isFirstChange: boolean = true;

  constructor(
    private formlyService: GaFormlyService,
    private messageService: GaMessageService,
    private reportFiltersUtil: ReportFiltersUtil,
    private reportGridService: ReportGridService
  ) {
    /**/
  }

  public get isEditMode():boolean {
    return this.mode === STORED_REPORT_EDIT_MODE;
  }

  private get isAddMode():boolean {
    return this.mode === STORED_REPORT_ADD_MODE;
  }

  public ngOnInit():void {
    this.model = {};
  }

  public ngOnChanges(changesObj:SimpleChanges):void {
    if (changesObj.mode) {
      this.mode = changesObj.mode.currentValue;
    }

    // initiate fields here because ngOnChanges triggered first before ngOnInit
    if (!this.fields && this.isEditMode) {
      this.fields = this.createFields();
    }

    //pull in changes to the bindings and update the fields
    if (changesObj.busy) {
      this.busy = changesObj.busy.currentValue;

      if (this.isEditMode) {
        this.storedReportsSelect.templateOptions.disabled = this.busy.loading || this.busy.saving || this.busy.deleting;
      }
    }

    if (this.isEditMode) {
      if (changesObj.storedReportId) {
        this.storedReportId = changesObj.storedReportId.currentValue;

        // should trigger this when it's not first time change
        // first time change will be handled by changesObj.storedReports
        // we don't want to trigger change 2 times (formly field onChange and here)
        // also we do != comparison because storedReportId possible string and number with same value (e.g. '1' and 1)
        // eslint-disable-next-line eqeqeq
        if (!changesObj.storedReportId.isFirstChange() && (changesObj.storedReportId.currentValue != changesObj.storedReportId.previousValue)) {
          // add timeout to let formControl is processed in DOM
          setTimeout(() => {
            this.selectReport();
          });
        } else {
          // Since ngOnChanges trigger when component lifecycle starts
          // This is for scenario when filters param is in state params but no 'storedReport' param per default (init load page)
          // When user select stored report for the first time this should ignore 'filters' state param to not overwrite stored report filters
          this.isFirstChange = !_.isUndefined(this.storedReportId);
        }
      }

      if (changesObj.storedReports) {
        // clear formly value when stored report id is undefined
        if (!this.storedReportId && this.storedReportsSelect?.formControl) {
          this.storedReportsSelect.formControl.setValue(undefined);
        }
        this.storedReports = changesObj.storedReports.currentValue;
        this.formlyService.fillField(this.storedReportsSelect, Promise.resolve(this.storedReports));

        // add timeout to let formControl is processed in DOM
        setTimeout(() => {
          this.selectReport();
        });
      }
    }
  }

  public editReport(): void {
    this.dialog = this.messageService.showDialog({
      component: StoredReportDialogComponent,
      size: DialogSize.XL,
      inputs: {
        busy: this.busy,
        metadata: this.metadata,
        filters: this.createInitialDialogFilters(),
        storedReports: this.storedReports,
        storedReport: this.model.storedReport,
        mode: this.mode
      },
      outputs: {
        onSaveStoredReport: this.onSaveStoredReport,
        onDeleteStoredReport: this.onDeleteReport,
        onDialogClose: (filterValue: IObject): void => {
          this.onDialogStateChange.emit({ isOpen: false, filters: filterValue });
          this.dialog.hide();
        }

      },
      wrapperComponent: false
    });

    this.dialog.onHide.subscribe(() => this.onDialogStateChange.emit({ isOpen: false }));
    this.onDialogStateChange.emit({ isOpen: true });
  }

  private onSaveStoredReport = (param: IStoredReportDialogParamEvent): void => {
    const storedReportId:number = this.isAddMode ? undefined : param.model?.report?.id;
    const ga1AddUrl:string = this.isAddMode ? undefined : param.model?.report?.ga1AddUrl;
    const browselistCode:string = this.isAddMode ? undefined : param.model?.report?.browselistCode;

    const storedReport:IStoredReport = {
      id: storedReportId,
      description: param.model.reportName,
      code: this.model.storedReport?.code,
      columns: this.reportFiltersUtil.convertFiltersToStoredReport(this.metadata, param.filters, this.reportGridService.getVisibleIndices('' + this.reportId)),
      category: param.model.category,
      sequence: param.model.sequence,
      ga1AddUrl,
      browselistCode
    };

    this.onSave.emit({
      reportId: this.reportId,
      storedReportId,
      storedReport
    });

    if (this.dialog) {
      this.onDialogStateChange.emit({ isOpen: false, filters: param.filters });
      this.dialog.hide();
    }
  };

  private onDeleteReport = (param: IStoredReportDialogParamEvent): void => {
    const storedReportId: number = param.model?.report?.id;
    this.onDelete.emit({ reportId: this.reportId, storedReportId });

    if (this.dialog) {
      this.onDialogStateChange.emit({ isOpen: false });
      this.dialog.hide();
    }
  };

  private createInitialDialogFilters(): any {
    // when mode is creating new (add) then use the currentFilters input bindings
    // when mode is edit existing stored report then extend the currentFilters input bindings with stored filters
    return this.isAddMode ? this.currentFilters : (this.model.storedReport ? util.copyAndExtend(this.reportFiltersUtil.convertStoredReportToFilters(this.metadata, this.model.storedReport), this.currentFilters) : {});
  }

  private selectReport():void {
    if (!this.storedReportId) { return; }

    const storedReport: IStoredReport = _.find(this.storedReports, (storedReport: IStoredReport) => (storedReport.code === this.storedReportId) || (storedReport.id === Number(this.storedReportId)));
    if (storedReport) {
      this.storedReportsSelect.formControl.setValue(storedReport);
    }
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTemplate('<h3 class="stored-reports-title">My Reports</h3>')
        .withWrapperClass('col')
        .build(),
      this.storedReportsSelect = this.formlyService.aNgSelect('storedReport')
        .withOptions(this.storedReports)
        .onChange((newStoredReport: IStoredReport) => {
          if (!this.isFirstChange) {
            this.onChange.emit({ storedReport: newStoredReport, allowOverwriteByFilterStateParam: this.isFirstChange });
          }
          this.isFirstChange = false;
        })
        .withFormat((sr:IStoredReport) => sr.sharedStoredReport ? `[Shared] ${sr.description}` : sr.description)
        .withValueClass('col-md-12 col-sm-12')
        .withLabelClass('d-none')
        .withWrapperClass('col-auto stored-reports-select')
        .withExtraOptions({
          templateOptions: {
            addInitialOption: false
          }
        })
        .build()
    ];
  }
}
